import React, { useState, useEffect } from "react";
import { FormattedMessage, FormattedNumber } from "react-intl";
import { extent } from "d3-array";
import { useSearchParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import MUISlider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";

import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";

import { CatalogStore, FilterStore } from "stores";

import Filter from "../../Filter";

import FinanceReducer from "./FinanceReducer";
import FinanceIndicator from "./FinanceIndicator";

import { NVehicle, TOfferCompressed, TRateCompressed } from "types";

import FinanceType from "./FinanceType";
import FinancePrice from "./FinancePrice";
import FinanceRate from "./FinanceRate";
// import FinanceCustomerType from "./FinanceCustomerType";

import { InputNumericValue } from "components/Input";
import { removeDeposit } from "helper/finance_helper";

interface ISideFilter {
  value: any;
  changeValue: any;
  ranges: any;
}

export const SideFilter: React.FC<ISideFilter> = ({
  value,
  changeValue,
  ranges,
}): JSX.Element | null => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ width: 1 }}>
      {/* <Box sx={{ mb: 2 }}>
        <FinanceCustomerType {...{ value, changeValue }} />
      </Box> */}
      <Box sx={{ mb: 2 }}>
        <FinanceType value={value.type} {...{ changeValue, ranges }} />
      </Box>

      <Collapse in={value.type === "buy"}>
        <Box sx={{ mb: 2 }}>
          <FinancePrice
            name="price"
            min={ranges.get("preis")[0]}
            max={ranges.get("preis")[1]}
            value={value.price}
            changeValue={changeValue}
          />
        </Box>
      </Collapse>
      {ranges.has("runtime") && (
        <Collapse in={"lease" === value.type}>
          <Box sx={{ mb: 2 }}>
            <FinanceRate
              name="rate"
              step={
                ranges.get("rate")[1] <= 200
                  ? 10
                  : ranges.get("rate")[1] <= 500
                    ? 50
                    : 100
              }
              min={ranges.get("rate")[0]}
              max={ranges.get("rate")[1]}
              value={value.rate}
              changeValue={changeValue}
            />
          </Box>
          <Opener
            toggleOpen={setOpen}
            open={open}
            title={
              <FormattedMessage id="filter.financial.refineSearch.label" />
            }
          />
          <Collapse in={open}>
            <Box>
              <Box sx={{ mb: 2 }}>
                <Deposit
                  name="deposit"
                  value={value.deposit}
                  max={10000}
                  changeValue={changeValue}
                />
              </Box>
              <RunTime
                name="runTime"
                min={ranges.get("runtime")[0]}
                max={ranges.get("runtime")[1]}
                value={value.runTime}
                changeValue={changeValue}
              />
              <Box sx={{ mb: 2 }}>
                <Mileage
                  value={value.mileage}
                  min={ranges.get("mileage")[0]}
                  step={ranges.get("mileage")[1] > 10000 ? 1000 : 500}
                  max={ranges.get("mileage")[1]}
                  changeValue={changeValue}
                />
              </Box>
            </Box>
            <Typography variant="h6" color="secondary">
              <FormattedMessage id="filter.financial.rateType.headline.label" />
            </Typography>
            {ranges.has("credit") && (
              <Credit value={value.credit} changeValue={changeValue} />
            )}
            {ranges.has("balloon") && (
              <Balloon value={value.balloon} changeValue={changeValue} />
            )}

            {ranges.has("leasing") && (
              <Leasing value={value.leasing} changeValue={changeValue} />
            )}
          </Collapse>
        </Collapse>
      )}
    </Box>
  );
};

export const SimpleFilter: React.FC<ISideFilter> = ({
  value,
  changeValue,
  ranges,
}): JSX.Element | null => {
  return (
    <>
      <Box
        sx={{
          gridColumn: "span 1",
        }}
      >
        <FinanceType
          value={value.type}
          {...{ changeValue, ranges }}
          variant="dropdown"
        />
      </Box>
      {value.type === "buy" && (
        <FinancePrice
          name="price"
          min={ranges.get("preis")[0]}
          max={ranges.get("preis")[1]}
          value={value.price}
          changeValue={changeValue}
          variant="dropdown+input"
        />
      )}
      {value.type === "lease" && (
        <FinanceRate
          name="rate"
          step={
            ranges.get("rate")[1] <= 200
              ? 10
              : ranges.get("rate")[1] <= 500
                ? 50
                : 100
          }
          min={ranges.get("rate")[0]}
          max={ranges.get("rate")[1]}
          value={value.rate}
          changeValue={changeValue}
          variant="dropdown+input"
        />
      )}
    </>
  );
};

const Slider = (props: any): JSX.Element => (
  <MUISlider
    getAriaLabel={(i) => i}
    {...props}
    sx={{
      "& .MuiSlider-markLabel[data-index='0']": {
        transform: "translateX(10%)",
      },
      "& .MuiSlider-markLabel[data-index='1']": {
        transform: "translateX(-110%)",
      },
    }}
  />
);

interface IOpener {
  toggleOpen: any;
  open: any;
  title: any;
}

const Opener: React.FC<IOpener> = ({ toggleOpen, open, title }) => (
  <Box
    sx={{
      display: "flex",
      borderBottom: 1,
      my: 3,
    }}
    onClick={() => toggleOpen(!open)}
    className="cursorHand"
  >
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="body2" color="secondary">
        {title}
      </Typography>
    </Box>
    <Box>
      {open ? (
        <ArrowDropUp fontSize="small" />
      ) : (
        <ArrowDropDown fontSize="small" />
      )}
    </Box>
  </Box>
);

interface IValue {
  value: any;
  name?: string;
  changeValue?: any;
}

interface ISlider extends IValue {
  value: any;
  min?: number;
  max: number;
  step?: number;
  disabled?: boolean;
}

export const Deposit: React.FC<ISlider> = ({
  value,
  name,
  min = 0,
  max,
  step,
  disabled,
  changeValue,
}) => {
  const submit = (newValue: any) => {
    changeValue(name, newValue);
  };
  return (
    <>
      <Typography variant="h6">
        <FormattedMessage id="filter.financial.deposit.title.label" />
      </Typography>
      <InputNumericValue
        onChangeHandler={submit}
        max={max}
        min={min}
        step={step}
        value={value}
        prefix="bis"
        unit="€"
      />
    </>
  );
};

const Credit: React.FC<IValue> = ({ changeValue, value }) => (
  <Bool
    changeValue={changeValue}
    label={
      <FormattedMessage id="filter.financial.rateType.options.credit.label" />
    }
    name="credit"
    value={value}
  />
);
const Balloon: React.FC<IValue> = ({ changeValue, value }) => (
  <Bool
    changeValue={changeValue}
    label={
      <FormattedMessage id="filter.financial.rateType.options.balloon.label" />
    }
    name="balloon"
    value={value}
  />
);

const Leasing: React.FC<IValue> = ({ changeValue, value }) => (
  <Bool
    changeValue={changeValue}
    label={
      <FormattedMessage id="filter.financial.rateType.options.leasing.label" />
    }
    name="leasing"
    value={value}
  />
);

interface IBool extends IValue {
  label: any;
  name: any;
  disabled?: boolean;
}

const Bool: React.FC<IBool> = ({
  value,
  changeValue,
  label,
  name,
  disabled = false,
}) => (
  <FormGroup row>
    <FormControlLabel
      disabled={disabled}
      control={
        <Checkbox
          checked={value}
          onChange={() => changeValue(name, !value)}
          value={value}
          color="primary"
        />
      }
      label={label}
    />
  </FormGroup>
);

export const RunTime: React.FC<ISlider> = ({
  name = "runTime",
  value = 36,
  changeValue,
  min = 12,
  max = 48,
  step = 6,
  disabled = false,
}) => {
  const submit = (newValue: any) => {
    changeValue(name, newValue);
  };
  return (
    <>
      <Typography variant="h6">
        <FormattedMessage id="filter.financial.runningTime.title.label" />
      </Typography>
      <InputNumericValue
        onChangeHandler={submit}
        max={max}
        min={min}
        step={step || getStep(max - min)}
        value={value}
        prefix="bis"
        suffix="Monate"
      />
    </>
  );
};

export const Mileage: React.FC<ISlider> = ({
  changeValue,
  name = "mileage",
  min = 0,
  max = 50000,
  step,
  value = 15000,
  disabled = false,
}) => {
  const submit = (newValue: any) => {
    changeValue(name, newValue);
  };
  return (
    <>
      <Typography variant="h6">
        <FormattedMessage id="filter.financial.mileage.title.label" />
      </Typography>
      <InputNumericValue
        onChangeHandler={submit}
        max={max}
        min={min}
        step={step || getStep(max - min)}
        value={value}
        prefix="bis"
        unit="km"
      />
    </>
  );
};

interface ICurrencySlider {
  changeValue: any;
  name: any;
  max: any;
  min: any;
  step?: any;
  title: any;
  value: any;
  disabled?: boolean;
}
export const CurrencySlider: React.FC<ICurrencySlider> = ({
  changeValue,
  name,
  max,
  min,
  step,
  title,
  value,
  disabled = false,
}) => {
  const [localValue, setLocalValue] = useState(0);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const submit = (_element: any, value: any) => {
    setLocalValue(value);
    changeValue(name, localValue);
  };

  const handleChange = (_element: any, value: any) => {
    setLocalValue(value);
  };

  const marks = [
    {
      value: min,
      label: <EURO value={min} />,
    },
    {
      value: max,
      label: <EURO value={max} />,
    },
  ];

  return (
    <Box className="slider" sx={{ opacity: disabled ? 0.3 : 1 }}>
      {title && (
        <Typography variant="h6" component="h3" color="secondary">
          {title}
        </Typography>
      )}
      <Typography align="right" variant="body2" color="secondary">
        {Array.isArray(localValue) && <EURO value={localValue[0]} />}
        &nbsp;
        <FormattedMessage id="common.to" defaultMessage="bis" />
        &nbsp;
        <EURO value={Array.isArray(localValue) ? localValue[1] : localValue} />
      </Typography>
      <Box sx={{ px: 1 }}>
        <Slider
          marks={marks}
          max={max}
          min={min}
          onChange={handleChange}
          onChangeCommitted={submit}
          step={step || getStep(max - min)}
          value={localValue}
        />
      </Box>
    </Box>
  );
};

export const getStep = (num: number): number => {
  const zeros = getZeros(num);
  const step =
    Math.round(num / (2 * Math.pow(10, zeros))) * Math.pow(10, zeros - 1);
  return step;
};

const getZeros = (num: number): number => {
  let zeros = -1;

  do {
    num = num / 10;
    ++zeros;
  } while (num > 1);
  return zeros;
};

interface IEURO {
  value: number;
}
export const EURO: React.FC<IEURO> = ({ value }): JSX.Element => (
  <FormattedNumber
    minimumFractionDigits={0}
    maximumFractionDigits={0}
    value={value}
    // eslint-disable-next-line react/style-prop-object
    style="currency"
    currency="EUR"
  />
);

export const getRanges = (
  vehicles: NVehicle[],
  customerType: string = "private",
): any => {
  const ranges = new Map();

  const preise = vehicles
    .map((e: any) => {
      const factor = customerType === "business" ? 1.19 : 1;
      return e.preis / factor;
    })
    .filter((e: number) => !!e);
  const preisRange = widen(extent(preise), 1000);
  ranges.set("preis", preisRange);

  const runtimes = vehicles
    .map((vehicle: any) =>
      vehicle.finance
        ? vehicle.finance
            .map((financeType: any) => {
              if (
                [
                  "leasing" + customerType,
                  "balloon" + customerType,
                  "credit" + customerType,
                  "leasing",
                  "balloon",
                  "credit",
                ].includes(financeType.type) === false
              )
                return [];
              const rates = financeType.rates
                .map((rate: any) => rate.runtime)
                .filter((runtime: number) => !!runtime)
                .flat();
              return rates;
            })
            .flat()
        : [],
    )
    .flat();

  const runtimeRange = widen(extent(runtimes), 6);
  if (!runtimeRange.length) return ranges;

  ranges.set("runtime", runtimeRange);

  const creditRates = vehicles
    .map((vehicle: any) =>
      vehicle.finance
        ? vehicle.finance
            .map((financeType: any) => {
              if (
                ["credit", "credit" + customerType].includes(
                  financeType.type,
                ) === false
              )
                return [];
              const factor =
                customerType === "business" && financeType.type === "credit"
                  ? 1.19
                  : 1;

              const rates = financeType.rates
                .map((rate: TRateCompressed) => {
                  const { runtime } = rate;
                  return rate.offers
                    .map(
                      (offer: TOfferCompressed): TOfferCompressed =>
                        removeDeposit(offer, runtime),
                    )
                    .map((offer: TOfferCompressed): number =>
                      offer[1] ? offer[1] / factor : 0,
                    )
                    .filter((monthyCost: number) => !!monthyCost);
                })
                .flat();
              return rates;
            })
            .flat()
        : [],
    )
    .flat();

  const creditRange = widen(extent(creditRates));
  creditRange.length && ranges.set("credit", creditRange);

  const leasingRates = vehicles
    .map((vehicle: any) =>
      vehicle.finance
        ? vehicle.finance
            .map((financeType: any) => {
              if (
                ["leasing", "leasing" + customerType].includes(
                  financeType.type,
                ) === false
              )
                return [];
              const factor =
                customerType === "business" && financeType.type === "leasing"
                  ? 1.19
                  : 1;
              const rates = financeType.rates
                .map((rate: TRateCompressed) => {
                  const { runtime } = rate;
                  return rate.offers
                    .map(
                      (offer: TOfferCompressed): TOfferCompressed =>
                        removeDeposit(offer, runtime),
                    )
                    .map((offer: TOfferCompressed): number =>
                      offer[1] ? offer[1] / factor : 0,
                    )
                    .filter((monthyCost: number) => !!monthyCost);
                })
                .flat();
              return rates;
            })
            .flat()
        : [],
    )
    .flat();

  const leasingRange = widen(extent(leasingRates));
  leasingRange.length && ranges.set("leasing", leasingRange);

  const balloonRates = vehicles
    .map((vehicle: any) =>
      vehicle.finance
        ? vehicle.finance
            .map((financeType: any) => {
              if (
                ["balloon", "balloon" + customerType].includes(
                  financeType.type,
                ) === false
              )
                return [];
              const factor =
                customerType === "business" && financeType.type === "balloon"
                  ? 1.19
                  : 1;
              const rates = financeType.rates
                .map((rate: TRateCompressed) => {
                  const { runtime } = rate;
                  return rate.offers
                    .map(
                      (offer: TOfferCompressed): TOfferCompressed =>
                        removeDeposit(offer, runtime),
                    )
                    .map((offer: TOfferCompressed): number =>
                      offer[1] ? offer[1] / factor : 0,
                    )
                    .filter((monthyCost: number) => !!monthyCost);
                })
                .flat();
              return rates;
            })
            .flat()
        : [],
    )
    .flat();

  const balloonRange = widen(extent(balloonRates));
  balloonRange.length && ranges.set("balloon", balloonRange);

  const REACT_APP_MINIMUM_RATE = process.env.REACT_APP_MINIMUM_RATE
    ? parseInt(process.env.REACT_APP_MINIMUM_RATE)
    : 0;

  const rates = [
    REACT_APP_MINIMUM_RATE,
    creditRange,
    leasingRange,
    balloonRange,
  ]
    .flat()
    .filter((e: number) => !!e);

  const rateRange = rates.length ? extent(rates) : [];

  rateRange.length && ranges.set("rate", rateRange);

  const mileages = vehicles
    .map((vehicle: any) =>
      vehicle.finance
        ? vehicle.finance
            .map((financeType: any) => {
              if (
                [
                  "balloon",
                  "balloon" + customerType,
                  "leasing",
                  "leasing" + customerType,
                ].includes(financeType.type) === false
              )
                return [];

              const rates = financeType.rates
                .map((rate: any) =>
                  rate.offers
                    .map((offer: any) => offer[0])
                    .filter((offer: number) => !!offer),
                )

                .flat();
              return rates;
            })
            .flat()
        : [],
    )
    .flat();

  const mileageRange = widen(extent(mileages), 2000);
  mileageRange.length && ranges.set("mileage", mileageRange);
  return ranges;
};

const widen = (input: [any, any], use_precision: number = 0) => {
  if (!(input && input.length === 2)) return [];
  if (isNaN(input[0]) || isNaN(input[0])) return [];

  const difference = input[1] - input[0];

  const precision =
    use_precision || (difference <= 200 ? 10 : difference <= 1000 ? 50 : 1000);

  const widened = [];
  widened.push(Math.floor(input[0] / precision) * precision);
  widened.push(Math.ceil(input[1] / precision) * precision);
  return widened;
};

export const getDefaultValue = (ranges: any) => {
  const filter = {
    type: "buy",
    customerType: "private",
    price: ranges.has("preis") && ranges.get("preis"),
    credit: !!ranges.has("credit"),
    balloon: !!ranges.has("balloon"),
    leasing: !!ranges.has("leasing"),
    deposit: 0,
    mileage: ranges.has("mileage") && ranges.get("mileage")[0],
    rate: ranges.has("rate") && ranges.get("rate"),
    runTime: ranges.has("runtime") && ranges.get("runtime")[1],
  };
  return filter;
};

interface IContainer {
  variant?: string;
}

const Container: React.FC<IContainer> = ({
  variant,
  ...props
}): JSX.Element | null => {
  const [ranges, setRanges]: [any, any] = useState();
  const [isPrivate, setIsPrivate]: [
    boolean,
    React.Dispatch<React.SetStateAction<boolean>>,
  ] = useState(true);

  const {
    state: {
      entities: { vehicle = null },
    },
  } = React.useContext(CatalogStore);

  const {
    state: { filter },
    dispatch: dispatchFilter,
  } = React.useContext(FilterStore);

  const [searchParams] = useSearchParams();
  React.useEffect(
    () => {
      dispatchFilter({
        type: "ADD_REDUCER",
        payload: { key: "finance", value: FinanceReducer },
      });
      dispatchFilter({
        type: "ADD_INDICATOR",
        payload: { key: "finance", value: FinanceIndicator },
      });
      const buyParams = searchParams.get("finance-buy");
      if (buyParams) {
        const params = buyParams.split(",").map((e) => parseInt(e));
        const value = { type: "buy", price: params };

        // dispatchFilter({
        //   type: "INIT_FILTER",
        //   payload: {
        //     value: { finance: value },
        //   },
        // });
      }
      const leaseParams = searchParams.get("finance-lease");
      if (leaseParams) {
        const params = leaseParams.split(",").map((e) => parseInt(e));
        const value = { type: "lease", rate: params };

        // dispatchFilter({
        //   type: "INIT_FILTER",
        //   payload: {
        //     value: { finance: value },
        //   },
        // });
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Statistics
  React.useEffect(
    () => {
      if (!vehicle) return;
      const vehicles = Object.values(vehicle);

      const ranges = getRanges(vehicles, isPrivate ? "private" : "business");
      setRanges(ranges);

      let defaultValue = getDefaultValue(ranges);
      let value = defaultValue;
      if (filter && filter.finance) {
        value = {
          ...defaultValue,
          type: filter.finance.type,
          customerType: isPrivate ? "private" : "business",
        };
      }

      dispatchFilter({
        type: "INIT_FILTER",
        payload: {
          value: { finance: value },
          reset: { finance: defaultValue },
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vehicle],
  );

  React.useEffect(() => {
    if (!vehicle) return;
    if (!filter.finance) return;
    const vehicles = Object.values(vehicle);
    const ranges = getRanges(vehicles, isPrivate ? "private" : "business");
    setRanges(ranges);
    const newValue = {
      ...value,
      price: ranges.get("preis"),
      rate: ranges.has("rate") && ranges.get("rate"),
      mileage: ranges.has("mileage") && ranges.get("mileage")[0],
      runTime: ranges.has("runtime") && ranges.get("runtime")[1],
    };

    dispatchFilter({ type: "UPDATE_FILTER", payload: { finance: newValue } });
  }, [isPrivate]);

  if (!filter.finance) return null;

  const { finance: value } = filter;

  const setValueHandler = (value: any) => {
    dispatchFilter({ type: "UPDATE_FILTER", payload: { finance: value } });
  };

  const submit = (value: any) => {
    setValueHandler(value);
  };

  const changeValue = (what: any, newValue: any) => {
    const newValues = { ...value, [what]: newValue };
    const resetter = !(
      newValues.credit ||
      newValues.balloon ||
      newValues.leasing
    )
      ? {
          credit: true,
          balloon: true,
          leasing: true,
        }
      : {};
    if (what === "customerType") {
      setIsPrivate(newValue === "private");
    }
    submit({ ...newValues, ...resetter });
  };
  if (!ranges) return null;
  if (variant === "simple")
    return <SimpleFilter {...props} {...{ value, changeValue, ranges }} />;
  return (
    <Filter
      {...{
        title: (
          <FormattedMessage
            id="filter.financial.title.label"
            defaultMessage="price / rate"
          />
        ),
        component: (
          <SideFilter {...props} {...{ value, changeValue, ranges }} />
        ),
      }}
    />
  );
};

export default Container;
